import { useState, useCallback, useEffect } from 'react'
import { X, Download, FileText, Loader2, ExternalLink } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { documentsApi } from '@/services/api'

const headingFont = { fontFamily: '"Outfit", sans-serif' }

interface DocumentPreviewModalProps {
    open: boolean
    onClose: () => void
    gcsPath: string
    startupId: string
    filename?: string
    page?: number | null
}

export function DocumentPreviewModal({
    open,
    onClose,
    gcsPath,
    startupId,
    filename,
    page,
}: DocumentPreviewModalProps) {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const loadPreview = useCallback(async () => {
        if (!gcsPath || !startupId) return

        setLoading(true)
        setError(null)
        setPreviewUrl(null)

        try {
            const result = await documentsApi.getPreviewUrl(gcsPath, startupId)
            let url = result.signed_url
            // Append page fragment for PDF page navigation
            if (page && page > 0) {
                url += `#page=${page}`
            }
            setPreviewUrl(url)
        } catch (err: any) {
            console.error('Failed to load document preview:', err)
            if (err.message?.includes('403')) {
                setError('You do not have permission to view this document.')
            } else if (err.message?.includes('404')) {
                setError('Document not found.')
            } else {
                setError('Failed to load document preview. Please try again.')
            }
        } finally {
            setLoading(false)
        }
    }, [gcsPath, startupId, page])

    // Load preview when modal opens (useEffect instead of onOpenChange,
    // because onOpenChange only fires on internal dialog state changes)
    useEffect(() => {
        if (open && gcsPath && startupId) {
            loadPreview()
        }
        if (!open) {
            // Reset state on close
            setPreviewUrl(null)
            setLoading(false)
            setError(null)
        }
    }, [open, gcsPath, startupId]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                onClose()
            }
        },
        [onClose]
    )

    const displayName = filename || extractFilename(gcsPath)
    const displayTitle = page ? `${displayName} — Page ${page}` : displayName

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0 gap-0 rounded-2xl overflow-hidden border-slate-200 dark:border-slate-700">
                <DialogTitle className="sr-only">{displayTitle}</DialogTitle>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center shrink-0">
                            <FileText className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="min-w-0">
                            <h3
                                className="font-bold text-slate-800 dark:text-slate-200 truncate text-sm"
                                style={headingFont}
                                title={displayTitle}
                            >
                                {displayTitle}
                            </h3>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                Document Preview
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {previewUrl && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-3 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                    onClick={() => window.open(previewUrl, '_blank')}
                                    title="Open in new tab"
                                >
                                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                    New Tab
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-3 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                    asChild
                                >
                                    <a href={previewUrl} download={displayName}>
                                        <Download className="w-3.5 h-3.5 mr-1.5" />
                                        Download
                                    </a>
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 bg-slate-100 dark:bg-slate-950 overflow-hidden" style={{ height: 'calc(95vh - 56px)' }}>
                    {loading && (
                        <div className="flex flex-col items-center justify-center h-full gap-4">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                                Loading document...
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center justify-center h-full gap-4 px-8">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center">
                                <FileText className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-sm font-bold text-red-600 dark:text-red-400 text-center">
                                {error}
                            </p>
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl font-bold"
                                onClick={loadPreview}
                            >
                                Try Again
                            </Button>
                        </div>
                    )}

                    {previewUrl && !loading && !error && (
                        <iframe
                            src={previewUrl}
                            className="w-full h-full border-none"
                            title={`Preview: ${displayName}`}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

/** Extract a readable filename from a GCS path like gs://bucket/org/startup/file.pdf */
function extractFilename(gcsPath: string): string {
    if (!gcsPath) return 'Document'
    // Remove gs:// prefix and hash fragments
    const cleanPath = gcsPath.replace(/^(source:|gs:\/\/[^/]+\/)/, '').split('#')[0]
    const parts = cleanPath.split('/')
    const filename = parts[parts.length - 1]
    // Decode URI components if encoded
    try {
        return decodeURIComponent(filename) || 'Document'
    } catch {
        return filename || 'Document'
    }
}
